// src/hooks/useSyncUser.js
import { useEffect, useRef } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { isRecoverableAuthError } from '../utils/auth0Errors';

const SIGNUP_ATTRIBUTION_STORAGE_KEY = 'dcfTeacherSignupAttribution';

const firstNonEmpty = (values) => {
  for (const value of values) {
    const cleaned = (value || '').trim();
    if (cleaned) {
      return cleaned;
    }
  }
  return null;
};

const readStoredSignupAttribution = () => {
  try {
    const raw = window.localStorage.getItem(SIGNUP_ATTRIBUTION_STORAGE_KEY);
    const parsed = raw ? JSON.parse(raw) : {};
    return parsed && typeof parsed === 'object' ? parsed : {};
  } catch (error) {
    return {};
  }
};

const captureSignupAttribution = () => {
  if (typeof window === 'undefined') {
    return {};
  }

  const stored = readStoredSignupAttribution();
  if (stored.source || stored.referrer) {
    return stored;
  }

  const params = new URLSearchParams(window.location.search);
  const source = firstNonEmpty([
    params.get('utm_source'),
    params.get('source'),
    params.get('ref'),
  ]);
  const referrer = firstNonEmpty([document.referrer]);
  const attribution = { source, referrer };

  if (source || referrer) {
    try {
      window.localStorage.setItem(
        SIGNUP_ATTRIBUTION_STORAGE_KEY,
        JSON.stringify(attribution),
      );
    } catch (error) {
      return attribution;
    }
  }

  return attribution;
};

const syncUserWithBackend = async ({ getAccessTokenSilently, signupAttribution }) => {
  console.log('User authenticated, attempting to sync with backend...');

  // 1. *** Get the ACCESS Token for your API ***
  // This uses the audience configured in Auth0Provider (index.js)
  console.log("Requesting Access Token..."); // Log before the call
  const accessToken = await getAccessTokenSilently({
    detailedResponse: false,
  }).catch((tokenError) => {
    if (isRecoverableAuthError(tokenError)) {
      // Session lacks API consent or refresh token; user must sign in again.
      return null;
    }
    throw tokenError;
  });
  console.log("Access Token received."); // Log after the call

  if (!accessToken) {
    return false;
  }

  // 2. Make the API Call to your backend
  // Ensure REACT_APP_API_BASE_URL is set in your frontend .env file
  // Example: REACT_APP_API_BASE_URL=http://localhost:8000/api
  const apiBaseUrl = process.env.REACT_APP_API_BASE_URL || '/api'; // Use the variable name consistent with previous steps

  // Construct the final URL. Make sure apiBaseUrl already includes /api if intended.
  // If REACT_APP_API_BASE_URL="http://localhost:8000/api", this is correct.
  // If REACT_APP_API_BASE_URL="http://localhost:8000", you'd need `${apiBaseUrl}/api/users/sync`
  const apiUrl = `${apiBaseUrl}/users/sync`;
  console.log("Calling API endpoint:", apiUrl); // Log the URL being called

  const response = await fetch(apiUrl, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      // *** Send the ACCESS Token ***
      'Authorization': `Bearer ${accessToken}`,
    },
    body: JSON.stringify({
      source: signupAttribution.source || null,
      referrer: signupAttribution.referrer || null,
    }),
  });

  if (!response.ok) {
    // Handle non-2xx responses
    const errorData = await response.json(); // Attempt to get JSON error details
    console.error('Failed to sync user:', response.status, errorData);
    return false;
  }

  await response.json();
  return true;
};

const useSyncUser = () => {
  // *** Get getAccessTokenSilently instead of getIdTokenClaims ***
  const { user, isAuthenticated, getAccessTokenSilently } = useAuth0();
  const syncedUserSubsRef = useRef(new Set()); // Prevent multiple syncs per user per session
  const syncingUserSubsRef = useRef(new Set());
  const userSub = user?.sub || null;

  useEffect(() => {
    if (
      !isAuthenticated
      || !userSub
      || syncedUserSubsRef.current.has(userSub)
      || syncingUserSubsRef.current.has(userSub)
    ) {
      return undefined;
    }

    const signupAttribution = captureSignupAttribution();
    syncingUserSubsRef.current.add(userSub);

    const syncAuthenticatedUser = async () => {
      try {
        const syncSucceeded = await syncUserWithBackend({
          getAccessTokenSilently,
          signupAttribution,
        });

        if (syncSucceeded) {
          syncedUserSubsRef.current.add(userSub);
        }
      } catch (error) {
        if (!isRecoverableAuthError(error)) {
          console.error('Error calling sync API or getting token:', error);
        }
      } finally {
        syncingUserSubsRef.current.delete(userSub);
      }
    };

    syncAuthenticatedUser();

  // Dependencies: Update to use getAccessTokenSilently
  }, [isAuthenticated, userSub, getAccessTokenSilently]);
};

export default useSyncUser;
